import { vuexfireMutations, firebaseAction } from 'vuexfire'
import * as types from '../mutation-types'
import * as MR from '@/libs/maybe-ramda'
import { stateManager } from '../helper'
import api from '@/libs/api'
import { fa } from '@/plugins/FirebaseAnalytics'
import container from '@/container'

const sm = stateManager(
  {
    user: null,
    claims: null,
    claimsReceived: false,
    metadata: null
  },
  {
    onSubmit: false
  }
)

export default {
  state: sm.get(),
  mutations: {
    [types.SET_ON_SUBMIT] (state, val) {
      state.onSubmit = val
    },
    [types.RECEIVE_USER] (state, { user }) {
      state.user = user
    },
    [types.CLAIMS_SET] (state, claims) {
      state.claims = claims
      state.claimsReceived = !!claims
    },
    [types.AUTH_RESET] (state) {
      sm.reset(state)
    },
    ...vuexfireMutations
  },
  actions: {
    login: authUser(true),
    signup: authUser(false),
    async logout ({ commit, state }) {
      const token = await state.user.getIdToken()
      commit(types.SET_ON_SUBMIT, true)
      const firebase = container.cradle.firebase
      await firebase.auth().signOut()
      fa.logEvent(fa.Event.LOGOUT)
      await api.recount(token)
    },
    loggedIn ({ commit, dispatch }, user) {
      commit(types.RECEIVE_USER, { user })
      const { errorTracking, analytics } = container.cradle
      errorTracking.setUser(user)
      analytics.setUser(user)

      const firebase = container.cradle.firebase
      const db = firebase.database()
      dispatch('setUserMetaRef', db.ref(`metadata/${user.uid}`)).then(() =>
        dispatch('updateClaims', { forceRefresh: true })
      )
      dispatch('initSettings', db.ref(`users/${user.uid}`))
      dispatch('setMetaRef', {
        ref: db.ref(`bookMeta/${user.uid}`),
        name: 'book'
      })
      dispatch('setMetaRef', {
        ref: db.ref(`memoMeta/${user.uid}`),
        name: 'memo'
      })
      dispatch('importer/setStateRef', db.ref(`importState/${user.uid}`))
      dispatch('purchases/logIn', user)
      dispatch('charts/fetch')
    },
    loggedOut ({ commit, dispatch }) {
      dispatch('purchases/reset')
      dispatch('resetUserData')
      dispatch('importer/clear')
      const { errorTracking, analytics } = container.cradle
      errorTracking.setUser(null)
      analytics.setUser(null)
    },
    async updateClaims ({ commit, state }, { forceRefresh }) {
      const res = await state.user.getIdTokenResult(forceRefresh)
      commit(types.CLAIMS_SET, res.claims)
    },
    setUserMetaRef: firebaseAction(({ bindFirebaseRef }, ref) => {
      return new Promise((resolve, reject) => {
        bindFirebaseRef('metadata', ref).then(resolve).catch(reject)
      })
    }),
    async checkPassword ({ state }, password) {
      const firebase = container.cradle.firebase
      const cred = firebase.auth.EmailAuthProvider.credential(
        state.user.email,
        password
      )

      try {
        await state.user.reauthenticateWithCredential(cred)
        return true
      } catch (e) {
        if (e.code === 'auth/wrong-password') {
          return false
        }

        throw e
      }
    },
    doneSubmitting ({ commit }) {
      commit(types.SET_ON_SUBMIT, false)
    }
  },
  getters: {
    isLoggedIn (state) {
      return !!state.user
    },
    user (state) {
      return state.user
    },
    uid (state) {
      return MR.of(state.user).chain(MR.prop('uid')).getOrElse(null)
    },
    isGuest (state) {
      return !state.user
    },
    isCustomer (_, getters) {
      return getters['purchases/hasPremium'] || getters.isEarlybird
    },
    isEarlybird (state) {
      return MR.of(state.claims).chain(MR.prop('earlybird')).getOrElse(false)
    },
    isAdmin (state) {
      return MR.of(state.claims).chain(MR.prop('admin')).getOrElse(false)
    },
    ocrQuota (state) {
      return MR.of(state.metadata)
        .chain(MR.path(['ocr', 'quota']))
        .getOrElse(0)
    }
  }
}

function authUser (isSignin = true) {
  const methodName = isSignin
    ? 'signInWithEmailAndPassword'
    : 'createUserWithEmailAndPassword'

  return async ({ commit }, creds) => {
    const firebase = container.cradle.firebase
    const auth = firebase.auth()
    commit(types.SET_ON_SUBMIT, true)
    await auth[methodName](creds.email, creds.password)
    fa.logEvent(isSignin ? fa.Event.SIGN_IN : fa.Event.SIGN_UP)
  }
}
